import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import PaymentModal from '../../components/PaymentModal';
import './PublicPages.css';
import { rates } from '../../data/exchangeRates';

const API_URL = process.env.REACT_APP_API_URL;
const stripePromise = loadStripe(process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY);

const QuoteDetailPage = ({ selectedCurrency }) => { // selectedCurrency prop'unu al
    const { token } = useParams();
    const navigate = useNavigate();
    const { t, i18n } = useTranslation();
    
    const [quote, setQuote] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
    const [clientSecret, setClientSecret] = useState('');

    useEffect(() => {
        const fetchQuote = async () => {
            try {
                setLoading(true); setError('');
                const response = await fetch(`${API_URL}/api/quotes/${token}`);
                const data = await response.json();
                if (!response.ok) {
                    throw new Error(t(data.messageKey) || t('error_quote_fetch'));
                }
                setQuote(data);
            } catch (err) { 
                setError(err.message);
            } finally { 
                setLoading(false); 
            }
        };
        fetchQuote();
    }, [token, t]);

    const handleAcceptAndPay = async () => {
        try {
            // DÜZELTME: Güvenlik ve standartlar gereği bu tür işlemler POST olmalı
            const response = await fetch(`${API_URL}/api/quotes/create-payment-intent-from-quote/${token}`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ targetCurrency: selectedCurrency }) // Hedef para birimini gönder
            });
            const data = await response.json();
            if (!response.ok) {
                throw new Error(data.error || 'Ödeme başlatılamadı.');
            }
            setClientSecret(data.clientSecret);
            setIsPaymentModalOpen(true);
        } catch (err) {
            setError(err.message);
        }
    };
    
    const handlePaymentSuccess = () => {
        setIsPaymentModalOpen(false);
        navigate('/order-success'); 
    };

    if (loading) return <div className="page-container public-page"><i className="fas fa-spinner fa-spin"></i> {t('loading_quote')}</div>;
    if (error) return <div className="page-container public-page error-message">{error}</div>;
    if (!quote) return null;

    const targetLangsArray = Array.isArray(quote.target_langs) ? quote.target_langs : JSON.parse(quote.target_langs);
    
    // Fiyatı, müşterinin Header'da seçtiği para birimine çevir
    const priceInQuoteCurrency = parseFloat(quote.price);
    const quoteCurrencyRate = rates[quote.currency] || 1;
    const selectedCurrencyRate = rates[selectedCurrency] || 1;
    const convertedPrice = (priceInQuoteCurrency / quoteCurrencyRate) / selectedCurrencyRate;

    const anyExtraService = quote.requires_dtp || quote.requires_shipping || quote.requires_notary;
    return (
        <>
            <div className="page-container public-page quote-page">
                <div className="quote-card">
                    <div className="quote-header">
                        <h2>{t('your_quote_details')}</h2>
                        <p>{t('quote_for_email', { email: quote.customer_email })}</p>
                    </div>
                     <div className="quote-body">
                        {/* DÜZELTME: info-grid artık tüm detayları içeriyor */}
                        <div className="info-grid quote-info-grid">
                            <div className="info-item"><span>{t('word_count')}</span><strong>{quote.word_count}</strong></div>
                            <div className="info-item"><span>{t('expertise_area')}</span><strong>{t(quote.expertise)}</strong></div>
                            <div className="info-item"><span>{t('translation_languages')}</span><strong>{quote.source_lang.toUpperCase()} &rarr; {targetLangsArray.join(', ').toUpperCase()}</strong></div>
                            <div className="info-item"><span>{t('order_class')}</span><strong>{t(quote.translator_type)}</strong></div>
                            <div className="info-item"><span>{t('delivery_speed')}</span><strong>{t(quote.delivery_speed === 'fast' ? 'fast_delivery' : 'standard_delivery')}</strong></div>
                            
                             {/* DÜZELTME: Ek hizmetler sadece seçilmişse (değeri 1 ise) gösterilir */}
                        {(quote.requires_dtp > 0 || quote.requires_shipping > 0 || quote.requires_notary > 0) && (
                            <div className="info-item full-width extra-services-quote">
                                <span>{t('extra_services')}</span>
                                <div className="service-tags">
                                    {quote.requires_dtp > 0 && <span className="service-tag">{t('dtp_service')}</span>}
                                    {quote.requires_shipping > 0 && <span className="service-tag">{t('shipping_service')}</span>}
                                    {quote.requires_notary > 0 && <span className="service-tag">{t('notary_service')}</span>}
                                </div>
                            </div>
                        )}
                    </div>
                           </div>
						   <div className="total-price-section-modern">
                        <span>{t('total_amount')}</span>
                        <strong>
                            {convertedPrice.toLocaleString(i18n.language, {
                                style: 'currency', 
                                currency: selectedCurrency
                            })}
                        </strong>
                    </div>
                    <div className="quote-footer">
                        <button className="cta-button" onClick={handleAcceptAndPay}>
                            {t('accept_and_pay_btn')}
                        </button>
                    </div>
                </div>
            </div>
            
            {isPaymentModalOpen && clientSecret && (
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                    <PaymentModal 
                        isOpen={isPaymentModalOpen}
                        onClose={() => setIsPaymentModalOpen(false)}
                        onPaymentSuccess={handlePaymentSuccess}
                    />
                </Elements>
            )}
        </>
    );
};

export default QuoteDetailPage;